import { useState } from 'react';
import { Plus, ChevronRight, FolderCog, BarChart3, List } from 'lucide-react';
import { useWorkspace } from '../WorkspaceContext';
import { useEditorContext } from '../../Editor/context';
import { CHAPTER_STATUS_LABELS } from '@xiaotianshu/shared';
import type { Chapter } from '../../../types';
import {
  PACE_FILTERS, SHORT_CHAPTER_WORDS, LONG_CHAPTER_WORDS,
  matchPaceFilter, paceFilterCounts, wordTone, chapterProgressPct,
  isTargetMet, plainSummary,
  type PaceFilter,
} from '../chapterPace';
// 平铺模式样式（独立文件：chapter-manager/ 分片受行数守卫约束，不能往里加）
import '../styles/chapterTree.css';
// 节奏视图复用既有时间线样式（原 VolumeOutline 用的那套，不重写）
import '../styles/timelineView.css';

/**
 * 侧栏章节列表。
 *
 * 两种**范围形态**（按卷数自适应）：
 *
 * | 场景 | 呈现 |
 * |---|---|
 * | 只有默认卷（新建作品、或不需要分卷的网文） | **平铺**：不出现卷层级 |
 * | 有 2 个以上卷，或唯一的卷不是默认卷 | **卷层级**（原有形态），每卷表头各自有 ＋ |
 *
 * 两种**视图模式**（顶部开关切换）：
 *
 * | 模式 | 用途 | 内容 |
 * |---|---|---|
 * | 大纲（默认） | 跳转导航 | 章节号 + 标题 + 状态点 + 字数 |
 * | 节奏 | 长篇审视 | 进度条 + 筛选（无摘要/短章/长章/未完成）+ 摘要预览（只读） |
 *
 * 为什么这样并：分卷此前是**硬前置** —— 新作品零分卷时这里什么都不渲染、也没有建章入口，
 * 用户必须先去「设定管理 → 卷目结构」建卷才能写第一章；而不少网文并不需要分卷。
 * 而「卷目结构」页真正独有的能力就是**节奏审视**（筛选 + 进度分层），
 * 它属于章节列表，不该另挂一个与"列表"重名的模块在设定管理下。
 * 该页原有的"摘要编辑"已去掉 —— 编辑器工具栏的「章节摘要」本就能改，无需两处入口。
 */
export function ChapterTree({ filterChapterIds }: { filterChapterIds?: Set<string> | null }) {
  const {
    volumes, chaptersMap, expandedVolumes,
    selectedChapter,
    toggleVolume, handleSelectChapter, handleContextMenu,
    setShowChapterModal, setShowVolumeManager,
    numberToChinese, getVolumeIndex, getChapterIndex,
  } = useWorkspace();
  const { activeView } = useEditorContext();
  const selectionActive = activeView === 'chapters';

  /** 视图模式：大纲（导航）/ 节奏（审视） */
  const [paceMode, setPaceMode] = useState(false);
  const [paceFilter, setPaceFilter] = useState<PaceFilter>('all');

  const ids = filterChapterIds ?? null;
  const filtering = ids !== null;

  /**
   * 平铺模式：整本书只有那一个自动创建的默认卷。
   * 此时卷层级对用户没有信息量（它没有名字上的区别、也不能删），隐去更干净。
   * 过滤态（全局搜索）不改变形态，只过滤章节。
   */
  const flatVolume = volumes.length === 1 && volumes[0].is_default ? volumes[0] : null;

  const allChapters: Chapter[] = volumes.flatMap(v => chaptersMap[v.id] ?? []);
  const counts = paceFilterCounts(allChapters);

  /** 节奏视图下：按筛选档 + 全局搜索过滤后的章节（跨卷展平） */
  const rhythmChapters = allChapters
    .filter(c => matchPaceFilter(c, paceFilter))
    .filter(c => (ids ? ids.has(c.id) : true))
    .sort((a, b) => a.chapter_number - b.chapter_number);

  const chapterIndexLabel = (chap: Chapter) => `第${numberToChinese(getChapterIndex(chap))}章`;

  /** 大纲模式的行（导航为主：状态点 + 标题 + 字数） */
  const outlineRow = (chap: Chapter) => {
    const isSelected = selectionActive && selectedChapter?.id === chap.id;
    const displayWordCount = chap.word_count ?? 0;
    const status = chap.status ?? 'draft';
    const target = chap.target_words ?? 0;
    // 有目标时显示进度，一眼看出本章还差多少字
    const wordLabel = target > 0
      ? `${displayWordCount}/${target}字`
      : (displayWordCount > 0 ? `${displayWordCount}字` : '');
    const targetMet = target > 0 && displayWordCount >= target;
    return (
      <div
        key={chap.id}
        className={`chapter-item ${isSelected ? 'chapter-item-selected' : ''}`}
        onClick={() => handleSelectChapter(chap)}
        onContextMenu={(e) => handleContextMenu(e, 'chapter', chap)}
      >
        <span
          className={`chapter-status-dot chapter-status-${status}`}
          title={CHAPTER_STATUS_LABELS[status]}
          aria-label={CHAPTER_STATUS_LABELS[status]}
        />
        <span className="chapter-title">{chapterIndexLabel(chap)} {chap.title}</span>
        {wordLabel && (
          <span className={`chapter-word-count${targetMet ? ' chapter-word-count-met' : ''}`}>{wordLabel}</span>
        )}
      </div>
    );
  };

  /** 节奏模式的行（审视为主：进度条 + 字数分层 + 摘要预览，只读不编辑） */
  const rhythmRow = (chap: Chapter) => {
    const isSelected = selectionActive && selectedChapter?.id === chap.id;
    const tone = wordTone(chap);
    const pct = chapterProgressPct(chap);
    const met = isTargetMet(chap);
    const summary = plainSummary(chap.summary || '');
    return (
      <div
        key={chap.id}
        className={`chapter-pace-row${isSelected ? ' chapter-pace-row--current' : ''}`}
        onClick={() => handleSelectChapter(chap)}
        onContextMenu={(e) => handleContextMenu(e, 'chapter', chap)}
      >
        <div className="chapter-pace-head">
          <span className="chapter-pace-number">{chapterIndexLabel(chap)}</span>
          <span
            className={`chapter-status-dot chapter-status-${chap.status ?? 'draft'}`}
            title={CHAPTER_STATUS_LABELS[chap.status ?? 'draft']}
            aria-label={CHAPTER_STATUS_LABELS[chap.status ?? 'draft']}
          />
          <span className="chapter-pace-title">{chap.title || '（未命名章节）'}</span>
          <span className={`chapter-pace-words is-${tone}`}>
            {(chap.word_count ?? 0).toLocaleString()} 字
            {chap.target_words > 0 && (
              <span className={met ? 'is-met' : ''}>{` / ${chap.target_words.toLocaleString()}`}</span>
            )}
          </span>
        </div>
        <div className={`chapter-pace-bar is-${tone}`}>
          <div className="chapter-pace-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className={`chapter-pace-summary${summary ? '' : ' is-empty'}`}>
          {summary || '暂无摘要'}
        </div>
      </div>
    );
  };

  /**
   * 顶部操作行。
   *
   * **不再设「新建章节」按钮**：章节列表本身就承载加章 —— 卷层级下每个卷表头右侧有 ＋
   * （加进该卷），平铺态下也有一个 ＋ 等价入口。两个按钮做同一件事只会让人犹豫点哪个。
   *
   * 改为一个「卷目管理」：卷的新建/改名/删除、以及**调整章节所属卷**都在那个弹窗里，
   * 这些操作此前散落在侧栏按钮 + 已删除的「卷目结构」页 + 右键菜单三处。
   */
  const headRow = () => (
    <div className="chapter-tree-head">
      <button className="chapter-tree-manage" onClick={() => setShowVolumeManager(true)}>
        <FolderCog size={13} /> 卷目管理
      </button>
      <button
        className={`chapter-tree-icon${paceMode ? ' is-active' : ''}`}
        onClick={() => setPaceMode(v => !v)}
        title={paceMode ? '切回大纲视图（只看标题与字数）' : '节奏视图：进度条与筛选，便于审视长篇节奏'}
        aria-label="节奏视图"
        aria-pressed={paceMode}
      >
        {paceMode ? <List size={13} /> : <BarChart3 size={13} />}
      </button>
    </div>
  );

  /** 节奏模式下的筛选行 */
  const paceFilterRow = () => (
    <div className="chapter-pace-filters">
      {PACE_FILTERS.map(f => (
        <button
          key={f.id}
          className={`chapter-pace-chip${paceFilter === f.id ? ' is-active' : ''}`}
          onClick={() => setPaceFilter(f.id)}
        >
          {f.label}
          <span className="chapter-pace-chip-count">{counts[f.id]}</span>
        </button>
      ))}
      <span className="chapter-pace-hint">短 ≤{SHORT_CHAPTER_WORDS} · 长 ≥{LONG_CHAPTER_WORDS}</span>
    </div>
  );

  const emptyHint = (label = '暂无章节') => <div className="chapter-empty">{label}</div>;

  // ── 节奏视图：跨卷展平，只看节奏 ────────────────────────────────────────
  if (paceMode) {
    return (
      <div className="chapter-tree">
        {headRow()}
        {paceFilterRow()}
        <div className="sidebar-content">
          <div className="chapter-pace-list">
            {rhythmChapters.map(rhythmRow)}
            {rhythmChapters.length === 0 && emptyHint(
              allChapters.length === 0 ? '暂无章节' : '该筛选条件下没有章节',
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── 大纲 · 平铺（只有默认卷） ──────────────────────────────────────────
  if (flatVolume) {
    const all = chaptersMap[flatVolume.id] || [];
    const chapters = ids ? all.filter(c => ids.has(c.id)) : all;
    return (
      <div className="chapter-tree">
        {/* 平铺态**没有卷表头**（卷被隐藏了），所以这里仍需一个加章入口 ——
            并把它做成显眼的主按钮：新作品进来第一件事就是建第一章。
            卷层级态则不需要（每个卷表头右侧已有 ＋）。 */}
        <div className="chapter-tree-head">
          <button
            className="chapter-tree-add"
            onClick={() => setShowChapterModal({ volumeId: flatVolume.id })}
            title="在本书末尾新增一章"
          >
            <Plus size={13} /> 新建章节
          </button>
          {headRow()}
        </div>
        <div className="sidebar-content">
          <div className="chapter-list">
            {chapters.map(outlineRow)}
            {chapters.length === 0 && emptyHint()}
          </div>
        </div>
      </div>
    );
  }

  // ── 大纲 · 卷层级（原有形态） ──────────────────────────────────────────
  const visibleVolumes = ids
    ? volumes.filter(vol => (chaptersMap[vol.id] || []).some(c => ids.has(c.id)))
    : volumes;

  return (
    <div className="chapter-tree">
      {headRow()}
      <div className="sidebar-content">
        {visibleVolumes.map(vol => {
          const all = chaptersMap[vol.id] || [];
          const chapters = ids ? all.filter(c => ids.has(c.id)) : all;
          const isExpanded = filtering ? true : expandedVolumes.includes(vol.id);
          const volumeIndex = getVolumeIndex(vol);
          const totalWords = chapters.reduce((acc, c) => acc + (c.word_count || 0), 0);
          return (
            <div key={vol.id} className="volume-group">
              <div
                className={`volume-header ${selectionActive && selectedChapter && chapters.some(c => c.id === selectedChapter.id) ? 'volume-header-active' : ''}`}
                onClick={() => { if (!filtering) toggleVolume(vol.id); }}
                onContextMenu={(e) => handleContextMenu(e, 'volume', vol)}
              >
                <ChevronRight size={12} className={`expand-icon ${isExpanded ? 'expand-icon-open' : ''}`} />
                <span className="volume-title">第{numberToChinese(volumeIndex)}卷 {vol.title}</span>
                <span className="volume-meta">{chapters.length}章 · {totalWords}字</span>
                {!filtering && (
                  <button onClick={(e) => { e.stopPropagation(); setShowChapterModal({ volumeId: vol.id }); }} className="icon-btn volume-add-chapter" title="新建章节">
                    <Plus size={12} />
                  </button>
                )}
              </div>
              {isExpanded && (
                <div className="chapter-list">
                  {chapters.map(outlineRow)}
                  {chapters.length === 0 && emptyHint()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
